<template>
    <div class="ticket-show-desk-host">
        <DeskIsland
            :initial-payload="deskPayload"
            :labels="labels"
            :ticket-labels="ticketLabels"
            :permissions="permissions"
            :current-user-id="currentUserId"
            :routes="routes"
            :assist-busy="assistBusy"
            :assist-action="assistAction"
            :assist-enabled="assistEnabled"
            :agent-chat="agentChat"
            @select-ticket="onSelectTicket"
            @save-operations="onSaveOperations"
            @save-assignee="onSaveAssignee"
            @take-ownership="onTakeOwnership"
            @close-ticket="onCloseTicket"
            @change-status="onChangeStatus"
            @merge-ticket="onMergeTicket"
            @send-message="onSendMessage"
            @send-ai-draft="onSendAiDraft"
            @discard-draft="onDiscardDraft"
            @clear-draft-ui="onClearDraftUi"
            @ricrea="onRicrea"
            @crea-bozza="onCreaBozza"
            @stop-assist="onStopAssist"
            @toast-error="toastError"
            @list-search="onListSearch"
            @list-load-more="onListLoadMore"
            :ticket-list-tickets="queueList.tickets"
            :ticket-list-total="queueList.total"
            :ticket-list-has-more="queueList.has_more"
            :ticket-list-loading="queueList.loading"
        />
    </div>
</template>

<script>
import DeskIsland from './DeskIsland.vue';
import { normalizeDeskPayload } from './normalizeDeskPayload';
import { parseAssistDraftOutput } from './parseAssistDraftOutput';
import { priorityRankForValue, sortOpenQueueTickets } from './sortOpenQueueTickets';
import { createAgentChatClient } from '@junction-ap/chat/agentChatClient';

export default {
    name: 'TicketShowDesk',
    components: { DeskIsland },
    props: {
        initialPayload: { type: Object, required: true },
        routes: { type: Object, required: true },
        permissions: { type: Object, required: true },
        labels: { type: Object, required: true },
        agentChat: { type: Object, required: true },
        csrfToken: { type: String, required: true },
        ticketLabels: { type: Object, required: true },
        currentUserId: { type: [String, Number], default: '' },
    },
    data() {
        return {
            payload: this.initialPayload,
            busy: false,
            draftId: this.initialPayload.composer?.pending_ai_draft?.id ?? null,
            assistBusy: false,
            assistAction: '',
            assistMode: '',
            chatClient: null,
            assistVariant: 0,
            assistRunId: '',
            assistAiStatusSnapshot: null,
            echoChannelName: '',
            deskSyncInFlight: false,
            deskSyncDebounceTimer: null,
            queueList: {
                tickets: [],
                total: 0,
                has_more: false,
                current_page: 1,
                search: '',
                loading: false,
                searchGeneration: 0,
            },
        };
    },
    computed: {
        deskPayload() {
            return normalizeDeskPayload(this.payload);
        },
        assistEnabled() {
            const agent = this.agentChat?.agent || this.agentChat?.default_agent || '';
            return typeof agent === 'string' && agent.trim() !== '';
        },
    },
    mounted() {
        this.syncQueueListFromPayload(this.payload);
        this.subscribeDeskLiveUpdates();
    },
    created() {
        this.chatClient = createAgentChatClient({
            endpoint: this.agentChat.endpoint,
            abortEndpoint: this.agentChat.abort_endpoint || '',
            csrfToken: () => this.csrfToken,
            getPins: () => ({
                agent: this.agentChat.agent || this.agentChat.default_agent || '',
                provider: this.agentChat.provider || 'openai',
                model: this.agentChat.model || this.agentChat.default_model || '',
            }),
            getRemember: () => false,
            getCompleteInBackground: () => true,
            getClientContext: () => ({
                ticket_id: this.payload?.ticket?.id ?? null,
                assist_mode: this.assistMode || null,
            }),
            onComplete: ({ content }) => {
                this.handleAssistComplete(content);
            },
            onError: (error) => {
                this.handleAssistError(error);
            },
        });
    },
    beforeUnmount() {
        this.unsubscribeDeskLiveUpdates();
        if (this.deskSyncDebounceTimer) {
            window.clearTimeout(this.deskSyncDebounceTimer);
            this.deskSyncDebounceTimer = null;
        }
        // Tab close / navigate: do not abort upstream while assist is busy (TM-BG-007).
        if (this.chatClient && !this.assistBusy) {
            this.chatClient.cancel();
        }
    },
    methods: {
        async request(url, options = {}) {
            this.busy = true;
            try {
                const response = await fetch(url, {
                    credentials: 'same-origin',
                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                        'X-CSRF-TOKEN': this.csrfToken,
                        ...(options.headers || {}),
                    },
                    ...options,
                });
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(data.message || 'Error');
                }
                return data;
            } finally {
                this.busy = false;
            }
        },
        toastSuccess(message) {
            if (window.SwalToast) {
                window.SwalToast.fire({ icon: 'success', title: message });
            }
        },
        toastError(message) {
            if (window.Swal) {
                window.Swal.fire({ icon: 'error', title: message });
            }
        },
        onSelectTicket(ticket) {
            if (ticket?.show_url) {
                window.location.href = ticket.show_url;
            }
        },
        async onSaveOperations({ priority }) {
            const tb = this.payload.desk_toolbar || {};
            try {
                const body = new URLSearchParams({
                    _method: 'PATCH',
                    status: tb.status,
                    priority: priority ?? tb.priority,
                    ticket_category_id: tb.ticket_category_id ?? '',
                });
                const data = await this.request(this.routes.operations, { method: 'POST', body });
                this.applyTicketPatch(data.ticket ?? data);
                this.toastSuccess(data.message);
            } catch (e) {
                this.toastError(e.message);
            }
        },
        async onChangeStatus({ status }) {
            const tb = this.payload.desk_toolbar || {};
            try {
                const body = new URLSearchParams({
                    _method: 'PATCH',
                    status,
                    priority: tb.priority ?? '',
                    ticket_category_id: tb.ticket_category_id ?? '',
                });
                const data = await this.request(this.routes.operations, { method: 'POST', body });
                this.applyTicketPatch(data.ticket ?? data);
                this.toastSuccess(data.message);
            } catch (e) {
                this.toastError(e.message);
            }
        },
        syncQueueListFromPayload(payload) {
            const list = payload?.ticket_list || {};
            const tickets = Array.isArray(list) ? list : (list.tickets || []);
            this.queueList = {
                ...this.queueList,
                tickets,
                total: list.total ?? tickets.length,
                has_more: Boolean(list.has_more),
                current_page: list.current_page ?? 1,
            };
        },
        async fetchDeskQueue({ page = 1, search = this.queueList.search, append = false } = {}) {
            if (!this.routes.deskQueue) {
                return;
            }
            const generation = this.queueList.searchGeneration + 1;
            this.queueList.searchGeneration = generation;
            this.queueList.loading = true;
            try {
                const params = new URLSearchParams({ page: String(page) });
                if (search && search.length >= 4) {
                    params.set('search', search);
                }
                const data = await this.request(`${this.routes.deskQueue}?${params.toString()}`, {
                    method: 'GET',
                });
                if (generation !== this.queueList.searchGeneration) {
                    return;
                }
                let mergedTickets = data.tickets || [];
                if (append) {
                    const seen = new Set(this.queueList.tickets.map((t) => String(t.id)));
                    mergedTickets = [
                        ...this.queueList.tickets,
                        ...mergedTickets.filter((t) => !seen.has(String(t.id))),
                    ];
                }
                this.queueList = {
                    ...this.queueList,
                    tickets: mergedTickets,
                    total: data.total ?? mergedTickets.length,
                    has_more: Boolean(data.has_more),
                    current_page: data.current_page ?? page,
                    search,
                    loading: false,
                };
                const list = this.payload.ticket_list || {};
                this.payload = {
                    ...this.payload,
                    ticket_list: {
                        ...(Array.isArray(list) ? {} : list),
                        tickets: mergedTickets,
                        total: this.queueList.total,
                        has_more: this.queueList.has_more,
                        current_page: this.queueList.current_page,
                    },
                };
            } catch (e) {
                if (generation === this.queueList.searchGeneration) {
                    this.queueList.loading = false;
                    this.toastError(e.message);
                }
            }
        },
        onListSearch(search) {
            this.queueList.search = search;
            this.fetchDeskQueue({ page: 1, search, append: false });
        },
        onListLoadMore() {
            if (this.queueList.loading || !this.queueList.has_more) {
                return;
            }
            this.fetchDeskQueue({
                page: (this.queueList.current_page || 1) + 1,
                search: this.queueList.search,
                append: true,
            });
        },
        async onSaveAssignee({ assignee }) {
            try {
                const assignedUserId = assignee === '' || assignee === null || assignee === undefined
                    ? null
                    : String(assignee);
                const body = new URLSearchParams({
                    assigned_user_id: assignedUserId ?? '',
                    context: 'desk',
                });
                const data = await this.request(this.routes.assign, { method: 'POST', body });
                this.applyAssignmentToPayload(data.ticket, assignedUserId);
                this.toastSuccess(data.message);
            } catch (e) {
                this.toastError(e.message);
            }
        },
        async onTakeOwnership() {
            try {
                const data = await this.request(this.routes.takeOwnership, {
                    method: 'POST',
                    body: new URLSearchParams(),
                });
                const assignedUserId = data.ticket?.assigned_user_id != null
                    ? String(data.ticket.assigned_user_id)
                    : String(this.currentUserId ?? '');
                this.applyAssignmentToPayload(data.ticket, assignedUserId);
                this.toastSuccess(data.message);
            } catch (e) {
                this.toastError(e.message);
            }
        },
        applyAssignmentToPayload(ticketPatch, assignedUserId) {
            this.applyTicketPatch(ticketPatch, { assignedUserId });
        },
        applyTicketPatch(ticketPatch, { assignedUserId } = {}) {
            if (!ticketPatch || typeof ticketPatch !== 'object') {
                return;
            }

            const tb = this.payload.desk_toolbar || {};
            const ticket = this.payload.ticket || {};
            const composer = this.payload.composer || {};
            const context = this.payload.context || {};
            const list = this.payload.ticket_list || {};
            const tickets = Array.isArray(list.tickets) ? list.tickets : (Array.isArray(list) ? list : []);
            const activeId = ticket.id;

            const status = ticketPatch.status;
            const statusLabel = ticketPatch.status_label;
            const priority = ticketPatch.priority;
            const priorityLabel = ticketPatch.priority_label;
            const derivedFlags = this.deskActionFlagsFromStatus(status);
            const isOpen = ticketPatch.is_open !== undefined ? ticketPatch.is_open : derivedFlags.is_open;
            const canClose = ticketPatch.can_close !== undefined ? ticketPatch.can_close : derivedFlags.can_close;
            const canBlock = ticketPatch.can_block !== undefined ? ticketPatch.can_block : derivedFlags.can_block;
            const canUnblock = ticketPatch.can_unblock !== undefined ? ticketPatch.can_unblock : derivedFlags.can_unblock;
            const removeFromList = status === 'closed' || status === 'merged';

            let nextTickets = removeFromList
                ? tickets.filter((row) => String(row.id) !== String(activeId))
                : tickets.map((row) => {
                    if (String(row.id) !== String(activeId)) {
                        return row;
                    }
                    return {
                        ...row,
                        ...(status ? { status, status_label: statusLabel || row.status_label } : {}),
                        ...(priority ? {
                            priority,
                            priority_label: priorityLabel || row.priority_label,
                            queue_sort_priority_rank: priorityRankForValue(priority),
                        } : {}),
                    };
                });

            if (!removeFromList && nextTickets.length > 1) {
                nextTickets = sortOpenQueueTickets(nextTickets);
            }

            this.payload = {
                ...this.payload,
                desk_toolbar: {
                    ...tb,
                    ...(assignedUserId !== undefined ? { assigned_user_id: assignedUserId } : {}),
                    ...(status ? { status } : {}),
                    ...(priority ? { priority } : {}),
                    ...(canClose !== undefined && canClose !== null ? { can_close: canClose } : {}),
                    ...(canBlock !== undefined && canBlock !== null ? { can_block: canBlock } : {}),
                    ...(canUnblock !== undefined && canUnblock !== null ? { can_unblock: canUnblock } : {}),
                },
                ticket: {
                    ...ticket,
                    ...(status ? { status, status_label: statusLabel || ticket.status_label } : {}),
                    ...(isOpen !== undefined && isOpen !== null ? { is_open: isOpen } : {}),
                },
                composer: {
                    ...composer,
                    ...(isOpen !== undefined && isOpen !== null ? { can_send_customer_reply: Boolean(isOpen) } : {}),
                },
                context: {
                    ...context,
                    ...(isOpen !== undefined && isOpen !== null ? { manual_link_enabled: Boolean(isOpen) } : {}),
                },
                ticket_list: Array.isArray(list)
                    ? nextTickets
                    : {
                        ...list,
                        tickets: nextTickets,
                    },
            };
        },
        deskActionFlagsFromStatus(status) {
            if (!status) {
                return {
                    is_open: undefined,
                    can_close: undefined,
                    can_block: undefined,
                    can_unblock: undefined,
                };
            }
            const closed = status === 'closed' || status === 'merged';
            return {
                is_open: !closed,
                can_close: !closed,
                // Mirrors TicketStatus::allowedTransitions → Blocked.
                can_block: status === 'assigned' || status === 'in_progress',
                can_unblock: status === 'blocked',
            };
        },
        async onCloseTicket() {
            try {
                const data = await this.request(this.routes.close, { method: 'POST', body: new URLSearchParams() });
                this.applyTicketPatch(data.ticket);
                this.toastSuccess(data.message);
            } catch (e) {
                this.toastError(e.message);
            }
        },
        async onMergeTicket({ targetTicketId, reason }) {
            try {
                const body = new URLSearchParams({
                    target_ticket_id: targetTicketId,
                    reason: reason ?? '',
                });
                const data = await this.request(this.routes.merge, { method: 'POST', body });
                this.toastSuccess(data.message);
                if (data.redirect_url) {
                    window.location.href = data.redirect_url;
                }
            } catch (e) {
                this.toastError(e.message);
            }
        },
        async onSendMessage({ type, text, files = [], mentionedUserIds = [] }) {
            const messageType = type === 'note' ? 'internal_note' : 'customer_response';
            try {
                const body = new FormData();
                body.set('message_type', messageType);
                body.set('body', text);
                if (messageType === 'internal_note') {
                    mentionedUserIds.forEach((id) => {
                        body.append('mentioned_user_ids[]', String(id));
                    });
                }
                (files || []).forEach((file) => {
                    body.append('attachments[]', file);
                });
                const data = await this.request(this.routes.message, { method: 'POST', body });
                this.toastSuccess(data.message);
                if (data.thread_message) {
                    this.appendThreadMessage(data.thread_message);
                    this.clearComposerAfterSend();
                    return;
                }
                if (data.internal_note) {
                    this.appendInternalNote(data.internal_note);
                    this.clearComposerAfterSend();
                    return;
                }
                if (data.reload) {
                    window.location.reload();
                }
            } catch (e) {
                this.toastError(e.message);
            }
        },
        appendThreadMessage(message) {
            const thread = this.payload.thread || {};
            const messages = [...(thread.messages || []), message];
            this.payload = {
                ...this.payload,
                thread: { ...thread, messages },
            };
        },
        appendInternalNote(note) {
            const context = this.payload.context || {};
            const notes = [...(context.internal_notes_timeline || []), note];
            this.payload = {
                ...this.payload,
                context: { ...context, internal_notes_timeline: notes },
            };
        },
        clearComposerAfterSend() {
            const composer = this.payload.composer || {};
            this.payload = {
                ...this.payload,
                composer: {
                    ...composer,
                    reply_body: '',
                    text: '',
                },
            };
        },
        clearAiDraftState() {
            this.draftId = null;
            const composer = this.payload.composer || {};
            this.payload = {
                ...this.payload,
                composer: {
                    ...composer,
                    reply_body: '',
                    text: '',
                    ai_draft_body: '',
                    pending_ai_draft: null,
                    ai_draft: false,
                    can_discard: false,
                    can_clear_ui: false,
                },
            };
        },
        async onSendAiDraft({ text, files = [] }) {
            const bodyText = String(text || '').trim();
            if (!bodyText) {
                return;
            }
            try {
                const body = new FormData();
                body.set('message_type', 'customer_response');
                body.set('body', bodyText);
                if (this.draftId) {
                    body.set('ticket_ai_draft_id', String(this.draftId));
                }
                (files || []).forEach((file) => {
                    body.append('attachments[]', file);
                });
                const data = await this.request(this.routes.message, { method: 'POST', body });
                this.toastSuccess(data.message);
                this.clearAiDraftState();
                await this.$nextTick();
                if (data.thread_message) {
                    this.appendThreadMessage(data.thread_message);
                    return;
                }
                if (data.reload) {
                    window.location.reload();
                }
            } catch (e) {
                this.toastError(e.message);
            }
        },
        onClearDraftUi() {
            this.clearAiDraftState();
        },
        async onDiscardDraft() {
            try {
                const data = await this.request(this.routes.discardDraft, { method: 'POST', body: new URLSearchParams() });
                this.clearAiDraftState();
                this.toastSuccess(data.message);
            } catch (e) {
                this.toastError(e.message);
            }
        },
        onRicrea({ text, instructions, useMcpTools }) {
            this.runAssist('ricrea', text, instructions, useMcpTools !== false);
        },
        onCreaBozza({ text, instructions, useMcpTools }) {
            this.runAssist('crea', text, instructions, useMcpTools !== false);
        },
        runAssist(action, reply, extra, useMcpTools = true) {
            if (this.assistBusy) {
                return;
            }
            if (!this.assistEnabled) {
                this.toastError(this.labels.assist_unavailable || 'Assistente AI non configurato per questo account');
                return;
            }

            const assist = (extra || '').trim();
            const body = (reply || '').trim();

            if (action === 'crea') {
                if (!assist) {
                    this.toastError(this.ticketLabels.aiInstructions || 'Istruzioni per l’AI richieste per Crea bozza');
                    return;
                }
            } else if (!body) {
                this.toastError(this.labels.ricrea || 'Bozza richiesta');
                return;
            }

            if (action === 'ricrea' && !assist) {
                this.toastError(this.ticketLabels.aiInstructions || 'Istruzioni per l’AI richieste per Ricrea');
                return;
            }

            this.assistVariant += 1;
            const ticketId = this.payload?.ticket?.id;
            const lines = [];
            if (action === 'crea') {
                lines.push('[CREA]');
            } else {
                lines.push('[RICREA]');
            }
            const skipMcp = (action === 'crea' || action === 'ricrea') && !useMcpTools;
            if (skipMcp) {
                lines.push('[NO_MCP]');
            }
            if (ticketId !== undefined && ticketId !== null && String(ticketId) !== '') {
                lines.push(`ticket_id: ${ticketId}`);
            }
            lines.push(`assist_nonce: ${this.assistVariant}`);
            const modePrompt = String(this.payload?.composer?.assist?.mode_prompt_block || '').trim();
            const skipModePrompt = skipMcp;
            if (modePrompt && !skipModePrompt) {
                lines.push(modePrompt);
            }
            const channelPrompt = String(this.payload?.channel_ai?.prompt_block || '').trim();
            if (channelPrompt) {
                lines.push(channelPrompt);
            }
            if (action === 'crea') {
                lines.push('Parole / traccia operatore:');
                lines.push(assist);
            } else {
                lines.push('Parole / traccia operatore:');
                lines.push(assist);
                lines.push('Bozza da ricreare:');
                lines.push(body);
            }

            this.assistBusy = true;
            this.assistAction = action;
            this.assistMode = action;
            this.snapshotAssistAiStatus();
            this.patchTicketAiStatus('generating_draft', this.generatingDraftLabel());
            this.chatClient.resetThread();
            this.assistRunId = `${ticketId || 't'}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const sendOptions = {
                completeInBackground: true,
                clientContext: {
                    ticket_id: ticketId,
                    assist_mode: action,
                    assist_run_id: this.assistRunId,
                    use_mcp_tools: useMcpTools,
                },
            };
            if (skipMcp) {
                sendOptions.useMcpTools = false;
            }
            this.chatClient.send(lines.join('\n'), sendOptions);
        },
        async onStopAssist() {
            if (!this.assistBusy || !this.chatClient) {
                return;
            }
            if (typeof this.chatClient.stop === 'function') {
                await this.chatClient.stop();
            } else {
                this.chatClient.cancel();
            }
            this.clearAssistBusy();
            this.restoreAssistAiStatusSnapshot();
            this.toastSuccess(this.labels.assist_stopped || 'Generazione interrotta');
        },
        generatingDraftLabel() {
            return this.labels.generating_draft
                || this.ticketLabels.generating_draft
                || this.labels.ticket_generating_draft
                || 'Generazione bozza in corso';
        },
        snapshotAssistAiStatus() {
            const ticket = this.payload.ticket || {};
            this.assistAiStatusSnapshot = {
                ai_status: ticket.ai_status ?? null,
                ai_status_label: ticket.ai_status_label ?? null,
            };
        },
        restoreAssistAiStatusSnapshot() {
            if (!this.assistAiStatusSnapshot) {
                return;
            }
            this.patchTicketAiStatus(
                this.assistAiStatusSnapshot.ai_status,
                this.assistAiStatusSnapshot.ai_status_label,
            );
            this.assistAiStatusSnapshot = null;
        },
        patchTicketAiStatus(aiStatus, aiStatusLabel) {
            const ticket = this.payload.ticket || {};
            this.payload = {
                ...this.payload,
                ticket: {
                    ...ticket,
                    ai_status: aiStatus,
                    ai_status_label: aiStatusLabel,
                },
            };
        },
        clearAssistBusy() {
            this.assistBusy = false;
            this.assistAction = '';
            this.assistMode = '';
        },
        async handleAssistComplete(content) {
            const raw = (typeof content === 'string' ? content : '').trim();
            const mode = this.assistMode;
            this.clearAssistBusy();

            if (!raw) {
                this.toastError('Nessuna bozza generata. Riprova.');
                return;
            }

            const parsed = parseAssistDraftOutput(raw);
            const applied = (parsed.draftBody || '').trim() || raw;
            const operatorFacts = parsed.operatorFacts;

            this.applyComposerDraft(applied);

            const runId = this.assistRunId;
            this.assistRunId = '';

            if (await this.adoptBackgroundAssistDraft(runId, applied)) {
                return;
            }

            if (!this.routes.assistDraft) {
                this.toastSuccess(this.labels.ai_draft_suggestion || 'Bozza AI pronta');
                if (operatorFacts) {
                    await this.persistAssistOperatorFacts(operatorFacts);
                }
                return;
            }

            try {
                const body = new URLSearchParams({
                    body_text: applied,
                    mode: mode || '',
                });
                const data = await this.request(this.routes.assistDraft, { method: 'POST', body });
                this.applyPersistedAssistDraft(data, applied);
                this.toastSuccess(data.message || this.labels.ai_draft_suggestion || 'Bozza AI salvata');
            } catch (e) {
                this.restoreAssistAiStatusSnapshot();
                this.toastError(e.message || 'Bozza generata ma non salvata');
                return;
            }

            if (operatorFacts) {
                await this.persistAssistOperatorFacts(operatorFacts);
            }
        },
        /**
         * The SSE run already persisted the draft (and its operator_facts note) server-side:
         * read that row instead of writing a second one from the desk.
         */
        async adoptBackgroundAssistDraft(runId, applied) {
            if (!runId || !this.routes.assistDraftPending) {
                return false;
            }
            try {
                const url = `${this.routes.assistDraftPending}?assist_run_id=${encodeURIComponent(runId)}`;
                const data = await this.request(url);
                if (!data || !data.draft_id) {
                    return false;
                }
                this.applyPersistedAssistDraft(data, applied);
                if (data.internal_note) {
                    this.appendInternalNote(data.internal_note);
                }
                this.toastSuccess(this.labels.ai_draft_suggestion || 'Bozza AI salvata');

                return true;
            } catch (e) {
                return false;
            }
        },
        applyPersistedAssistDraft(data, applied) {
            this.draftId = data.draft_id ?? this.draftId;
            const composer = this.payload.composer || {};
            const ticket = this.payload.ticket || {};
            this.payload = {
                ...this.payload,
                ticket: {
                    ...ticket,
                    ai_status: data.ai_status ?? ticket.ai_status,
                    ai_status_label: data.ai_status_label ?? ticket.ai_status_label,
                },
                composer: {
                    ...composer,
                    ai_draft_body: applied,
                    ai_draft: true,
                    can_discard: true,
                    can_clear_ui: true,
                    pending_ai_draft: {
                        id: data.draft_id,
                        body_text: applied,
                        confidence: composer.pending_ai_draft?.confidence ?? null,
                    },
                },
            };
            this.assistAiStatusSnapshot = null;
        },
        /**
         * Persist operator_facts as an internal note after assist — do not clear the composer.
         */
        async persistAssistOperatorFacts(operatorFacts) {
            const text = String(operatorFacts || '').trim();
            if (!text || !this.routes.message) {
                return;
            }
            try {
                const body = new FormData();
                body.set('message_type', 'internal_note');
                body.set('body', text);
                const data = await this.request(this.routes.message, { method: 'POST', body });
                if (data.internal_note) {
                    this.appendInternalNote(data.internal_note);
                }
            } catch (e) {
                this.toastError(e.message || 'Bozza salvata; nota interna non salvata');
            }
        },
        handleAssistError(error) {
            this.clearAssistBusy();
            this.restoreAssistAiStatusSnapshot();
            const status = error?.httpStatus;
            const code = error?.errorCode;
            let title = error?.message || 'Errore assistente AI';
            if (code === 'provider_auth' || status === 401 || status === 403) {
                title = 'Chiave API provider mancante o non valida (401/403).';
            }
            this.toastError(title);
        },
        applyComposerDraft(text) {
            const body = (text || '').trim();
            if (!body) {
                return;
            }
            const composer = this.payload.composer || {};
            this.payload = {
                ...this.payload,
                composer: {
                    ...composer,
                    ai_draft_body: body,
                    ai_draft: true,
                    can_discard: Boolean(composer.pending_ai_draft || this.draftId),
                    can_clear_ui: true,
                },
            };
        },
        subscribeDeskLiveUpdates() {
            const userId = this.currentUserId || window.Laravel?.userId;
            if (!window.Echo || !userId) {
                return;
            }
            this.echoChannelName = `App.User.${userId}`;
            window.Echo.private(this.echoChannelName)
                .listen('.ticket.desk.updated', (event) => {
                    this.onDeskLiveEcho(event);
                });
        },
        unsubscribeDeskLiveUpdates() {
            if (!window.Echo || !this.echoChannelName) {
                return;
            }
            window.Echo.private(this.echoChannelName)
                .stopListening('.ticket.desk.updated');
        },
        onDeskLiveEcho(event) {
            const ticketId = event?.ticket_id;
            const activeId = this.payload?.ticket?.id;
            if (ticketId === undefined || ticketId === null || String(ticketId) !== String(activeId)) {
                return;
            }
            const reason = String(event?.reason || '');
            if (this.deskSyncDebounceTimer) {
                window.clearTimeout(this.deskSyncDebounceTimer);
            }
            this.deskSyncDebounceTimer = window.setTimeout(() => {
                this.deskSyncDebounceTimer = null;
                this.refreshDeskFromServer(reason);
            }, 200);
        },
        async refreshDeskFromServer(reason) {
            if (!this.routes.deskSync || this.deskSyncInFlight) {
                return;
            }
            this.deskSyncInFlight = true;
            try {
                const data = await this.request(this.routes.deskSync, { method: 'GET' });
                this.mergeLiveSyncPayload(data, reason);
            } catch (_) {
                // Echo is best-effort; operator can still reload manually.
            } finally {
                this.deskSyncInFlight = false;
            }
        },
        mergeLiveSyncPayload(data, reason) {
            if (!data || typeof data !== 'object') {
                return;
            }

            const ticket = this.payload.ticket || {};
            const thread = this.payload.thread || {};
            const context = this.payload.context || {};
            const composer = this.payload.composer || {};

            const serverThread = data.thread?.messages;
            const serverNotes = data.context?.internal_notes_timeline;
            const serverTicket = data.ticket || {};
            const serverComposer = data.composer || {};

            let nextPayload = {
                ...this.payload,
                ticket: {
                    ...ticket,
                    ...(serverTicket.ai_status !== undefined
                        ? { ai_status: serverTicket.ai_status, ai_status_label: serverTicket.ai_status_label }
                        : {}),
                    ...(serverTicket.sentiment_label !== undefined
                        ? { sentiment_label: serverTicket.sentiment_label }
                        : {}),
                },
            };

            if (Array.isArray(serverThread)) {
                nextPayload = {
                    ...nextPayload,
                    thread: { ...thread, messages: serverThread },
                };
            }

            if (Array.isArray(serverNotes)) {
                nextPayload = {
                    ...nextPayload,
                    context: { ...context, internal_notes_timeline: serverNotes },
                };
            }

            const pendingDraft = serverComposer.pending_ai_draft;
            const draftBody = pendingDraft?.body_text
                ? String(pendingDraft.body_text)
                : String(serverComposer.reply_body || '').trim();

            if (reason === 'ai_draft_ready' && draftBody && this.shouldApplyAutoDraft(serverComposer)) {
                nextPayload.composer = {
                    ...composer,
                    ...serverComposer,
                    ai_draft_body: draftBody,
                    ai_draft: true,
                    can_discard: true,
                    can_clear_ui: true,
                };
                if (pendingDraft?.id) {
                    this.draftId = pendingDraft.id;
                }
            } else if (reason === 'ai_draft_ready' && draftBody && !this.shouldApplyAutoDraft(serverComposer)) {
                const toastMsg = this.labels.new_draft_available
                    || this.ticketLabels.new_draft_available
                    || 'Nuova bozza AI disponibile — aggiorna o svuota il composer per applicarla.';
                this.toastSuccess(toastMsg);
            }

            this.payload = nextPayload;
        },
        shouldApplyAutoDraft(serverComposer) {
            if (this.assistBusy) {
                return false;
            }
            const composer = this.payload.composer || {};
            const currentDraft = String(composer.ai_draft_body || '').trim();
            if (!currentDraft) {
                return true;
            }
            const prevDraftId = composer.pending_ai_draft?.id ?? this.draftId;
            const nextDraftId = serverComposer.pending_ai_draft?.id;
            if (prevDraftId && nextDraftId && String(prevDraftId) === String(nextDraftId)) {
                return true;
            }

            return false;
        },
    },
};
</script>
